import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { workwiseServices, padServices } from '../../../api';
import { setDocumentInformation, setTemplateData } from '../../../app/EditorSlice';
import './RenameModal.css';

const RenameModal = ({ visible, onClose, documentInformation }) => {

    const { id } = useParams();
    const dispatch = useDispatch();
    const isTemplateDocument = useSelector((state) => state.editor.isTemplateDocument);
    const templateData = useSelector((state) => state.editor.templateData);
    const [newName, setNewName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        // prefill and reset states on open
        if (visible) {
            const currentName = isTemplateDocument ? templateData?.name : documentInformation?.name;
            setNewName(currentName || '');
            setError('');
            // focus and select the input for quick rename
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 0);
        }
    }, [visible, documentInformation, templateData, isTemplateDocument]);

    const handleRename = async () => {
        const trimmed = newName.trim();
        if (!trimmed) {
            setError('Name cannot be empty');
            message.error('Name cannot be empty');
            return;
        }

        // no length limit validation - any non-empty value is accepted
        try {
            setLoading(true);
            if (isTemplateDocument) {
                await padServices.updateTemplate({ templateId: id, data: { name: trimmed }, dispatch });
                message.success('Template renamed successfully');
            } else {
                const payload = {
                    attachmentId: id,
                    name: trimmed,
                    privacyId: documentInformation?.privacyId || 1,
                    members: documentInformation?.members || []
                };
                await workwiseServices.updateDocumentName(payload);
                message.success('Document renamed successfully');
            }
            dispatch(isTemplateDocument
                ? setTemplateData({ ...templateData, name: trimmed })
                : setDocumentInformation({ ...documentInformation, name: trimmed })
            );
            onClose();
        } catch (e) {
            console.error('Failed to rename document:', e);
            message.error('Failed to rename document. Please try again.');
        }
        finally {
            setLoading(false);
        }
    };

    const isEmpty = newName.trim() === '';
    const canRename = !isEmpty && !loading;

    return (
        <Modal
            title={<span className="modal-title"><EditOutlined /> {isTemplateDocument ? 'Rename Template' : 'Rename Document'}</span>}
            visible={visible}
            onOk={handleRename}
            onCancel={onClose}
            okText="Rename"
            cancelText="Cancel"
            className="rename-modal"
            centered
            width={420}
            okButtonProps={{ disabled: !canRename, loading }}
        >
            <div className="modal-body">
                <label className="input-label">New {isTemplateDocument ? 'Template' : 'Document'} Name <span className="required">*</span></label>
                <Input
                    ref={inputRef}
                    value={newName}
                    onChange={(e) => {
                        const value = e.target.value;
                        setError('');
                        setNewName(value);
                    }}
                    placeholder={`Enter new ${isTemplateDocument ? 'template' : 'document'} name`}
                    onPressEnter={handleRename}
                    className="rename-input"
                    aria-label={`New ${isTemplateDocument ? 'template' : 'document'} name`}
                    aria-describedby={error ? 'rename-helper-text' : undefined}
                    aria-invalid={!!error}
                    allowClear
                />
                {error && (
                    <div className="rename-meta">
                        <span className="error-text" id="rename-helper-text">{error}</span>
                    </div>
                )}
                <p className="helper-text">
                    This will update the {isTemplateDocument ? 'template' : 'document'} name for all collaborators.
                </p>
            </div>
        </Modal>
    );
};

export default RenameModal;